"use client";

import { useState, useCallback, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Settings, Zap, MousePointerClick, Info, Paperclip, X, Save, FileText, BookOpen, Library, Book, Plus, Trash2, ExternalLink, Upload, Link as LinkIcon, GripVertical, Pencil, CheckSquare, Eye, EyeOff, AlertTriangle, Check, ChevronsUpDown, Search } from "lucide-react";
import { toast } from "sonner";

import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import { getVersion, updateVersion } from "@/lib/api/versions";
import { listAllFormFieldGroups } from "@/lib/api/form-fields";
import { uploadAttachment, formatFileSize } from "@/lib/api/attachments";
import { getErrorMessage } from "@/lib/api/client";
import type { SimulatorSettings, ResourceItem, GlossaryTerm, GroupValidationSettings, FormFieldGroupWithFields, CrossFieldWarningRule, FormField, WarningDisplayMode, WarningSeverity } from "@/types";

// Helper to generate unique IDs
const generateId = () => crypto.randomUUID();

// Get content type icon info
function getFileTypeLabel(contentType: string): string {
  if (contentType === "application/pdf") return "PDF";
  if (contentType.includes("word") || contentType.includes("document")) return "Document Word";
  if (contentType.includes("excel") || contentType.includes("spreadsheet")) return "Tableur Excel";
  if (contentType.includes("powerpoint") || contentType.includes("presentation")) return "Présentation";
  if (contentType.startsWith("image/")) return "Image";
  if (contentType.includes("zip") || contentType.includes("rar") || contentType.includes("7z")) return "Archive";
  return "Fichier";
}

// Common file type options
const FILE_TYPE_OPTIONS = [
  { value: "pdf", label: "PDF", category: "Documents" },
  { value: "doc", label: "DOC", category: "Documents" },
  { value: "docx", label: "DOCX", category: "Documents" },
  { value: "xls", label: "XLS", category: "Documents" },
  { value: "xlsx", label: "XLSX", category: "Documents" },
  { value: "ppt", label: "PPT", category: "Documents" },
  { value: "pptx", label: "PPTX", category: "Documents" },
  { value: "txt", label: "TXT", category: "Documents" },
  { value: "csv", label: "CSV", category: "Documents" },
  { value: "jpg", label: "JPG", category: "Images" },
  { value: "png", label: "PNG", category: "Images" },
  { value: "gif", label: "GIF", category: "Images" },
  { value: "webp", label: "WEBP", category: "Images" },
  { value: "svg", label: "SVG", category: "Images" },
  { value: "zip", label: "ZIP", category: "Archives" },
  { value: "rar", label: "RAR", category: "Archives" },
  { value: "7z", label: "7Z", category: "Archives" },
];

interface SettingsTabProps {
  versionId: number;
  simulatorId: number;
  isEditable: boolean;
}

export function SettingsTab({ versionId, simulatorId, isEditable }: SettingsTabProps) {
  const queryClient = useQueryClient();

  // Fetch version data for settings
  const { data: versionData, isLoading } = useQuery({
    queryKey: ["version", versionId],
    queryFn: () => getVersion(versionId),
    enabled: !!versionId,
  });
  
  // Fetch form field groups for group validation settings
  const { data: formFieldGroups } = useQuery({
    queryKey: ["formFieldGroups", versionId],
    queryFn: () => listAllFormFieldGroups(versionId),
    enabled: !!versionId,
  });

  // Update version settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: (newSettings: SimulatorSettings) =>
      updateVersion(versionId, { settings: newSettings }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["version", versionId] });
      queryClient.invalidateQueries({ queryKey: ["simulator", simulatorId] });
      toast.success("Paramètres mis à jour");
    },
    onError: (error) => {
      toast.error("Erreur", { description: getErrorMessage(error) });
    },
  });

  // Current settings with defaults
  const settings: SimulatorSettings = versionData?.settings || {};
  const autoCalculate = settings.auto_calculate ?? false;
  const autoSaveDraft = settings.auto_save_draft ?? true;  // Default to true for manual mode
  const enableAttachments = settings.enable_attachments ?? false;
  const maxAttachments = settings.max_attachments ?? 0;
  const allowedFileTypes = settings.allowed_file_types ?? [];
  const enableNotes = settings.enable_notes ?? true;  // Default to true to keep existing behavior
  
  // Output display settings
  const hideZeroOutputs = settings.hide_zero_outputs ?? false;
  const showAllOutputsButton = settings.show_all_outputs_button ?? false;
  
  // Proposal settings
  const hideZeroTableRowsInProposals = settings.hide_zero_table_rows_in_proposals ?? false;
  
  // Local state for max attachments input
  const [maxAttachmentsInput, setMaxAttachmentsInput] = useState(maxAttachments.toString());

  // Handler for auto_calculate toggle
  const handleAutoCalculateChange = (checked: boolean) => {
    updateSettingsMutation.mutate({ ...settings, auto_calculate: checked });
  };
  
  // Handler for auto_save_draft toggle
  const handleAutoSaveDraftChange = (checked: boolean) => {
    updateSettingsMutation.mutate({ ...settings, auto_save_draft: checked });
  };
  
  // Handler for enable_attachments toggle
  const handleEnableAttachmentsChange = (checked: boolean) => {
    updateSettingsMutation.mutate({ ...settings, enable_attachments: checked });
  };
  
  // Handler for enable_notes toggle
  const handleEnableNotesChange = (checked: boolean) => {
    updateSettingsMutation.mutate({ ...settings, enable_notes: checked });
  };
  
  // Handler for hide_zero_outputs toggle
  const handleHideZeroOutputsChange = (checked: boolean) => {
    updateSettingsMutation.mutate({ ...settings, hide_zero_outputs: checked });
  };
  
  // Handler for show_all_outputs_button toggle
  const handleShowAllOutputsButtonChange = (checked: boolean) => {
    updateSettingsMutation.mutate({ ...settings, show_all_outputs_button: checked });
  };
  
  const handleHideZeroTableRowsInProposalsChange = (checked: boolean) => {
    updateSettingsMutation.mutate({ ...settings, hide_zero_table_rows_in_proposals: checked });
  };
  
  // Handler for max_attachments
  const handleMaxAttachmentsChange = (value: string) => {
    setMaxAttachmentsInput(value);
    const num = parseInt(value, 10);
    if (!isNaN(num) && num >= 0) {
      updateSettingsMutation.mutate({ ...settings, max_attachments: num });
    }
  };
  
  // Handler for file type toggle
  const handleFileTypeToggle = (fileType: string) => {
    const newTypes = allowedFileTypes.includes(fileType)
      ? allowedFileTypes.filter(t => t !== fileType)
      : [...allowedFileTypes, fileType];
    updateSettingsMutation.mutate({ ...settings, allowed_file_types: newTypes });
  };
  
  // Handler to clear all file type restrictions
  const handleClearFileTypes = () => {
    updateSettingsMutation.mutate({ ...settings, allowed_file_types: [] });
  };
  
  // Handler to select all file types
  const handleSelectAllFileTypes = () => {
    updateSettingsMutation.mutate({ ...settings, allowed_file_types: FILE_TYPE_OPTIONS.map(f => f.value) });
  };

  // ============ FOREWORD SETTINGS ============
  const enableForeword = settings.enable_foreword ?? false;
  const forewordTitle = settings.foreword_title ?? "";
  const forewordContent = settings.foreword_content ?? "";
  
  // Local state for foreword editing (debounced save)
  const [localForewordTitle, setLocalForewordTitle] = useState(forewordTitle);
  const [localForewordContent, setLocalForewordContent] = useState(forewordContent);
  
  // Sync local state when settings change (e.g., from server)
  useState(() => {
    setLocalForewordTitle(forewordTitle);
    setLocalForewordContent(forewordContent);
  });
  
  const handleEnableForewordChange = (checked: boolean) => {
    updateSettingsMutation.mutate({ ...settings, enable_foreword: checked });
  };
  
  const handleForewordTitleBlur = () => {
    if (localForewordTitle !== forewordTitle) {
      updateSettingsMutation.mutate({ ...settings, foreword_title: localForewordTitle || undefined });
    }
  };
  
  const handleForewordContentBlur = () => {
    if (localForewordContent !== forewordContent) {
      updateSettingsMutation.mutate({ ...settings, foreword_content: localForewordContent || undefined });
    }
  };

  // ============ RESOURCES SETTINGS ============
  const enableResources = settings.enable_resources ?? false;
  const resourcesTitle = settings.resources_title ?? "";
  const resources = settings.resources ?? [];
  
  // Local state for resources editing
  const [localResourcesTitle, setLocalResourcesTitle] = useState(resourcesTitle);
  const [resourceDialogOpen, setResourceDialogOpen] = useState(false);
  const [editingResource, setEditingResource] = useState<ResourceItem | null>(null);
  const [resourceInputMode, setResourceInputMode] = useState<"link" | "file" | "upload">("link");
  const [newResource, setNewResource] = useState<Partial<ResourceItem>>({
    type: "link",
    title: "",
    description: "",
    url: "",
  });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFileInfo, setUploadedFileInfo] = useState<{ name: string; size: number; type: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const handleEnableResourcesChange = (checked: boolean) => {
    updateSettingsMutation.mutate({ ...settings, enable_resources: checked });
  };
  
  const handleResourcesTitleBlur = () => {
    if (localResourcesTitle !== resourcesTitle) {
      updateSettingsMutation.mutate({ ...settings, resources_title: localResourcesTitle || undefined });
    }
  };
  
  const handleAddResource = () => {
    if (!newResource.title || !newResource.url) {
      toast.error("Veuillez remplir le titre et l'URL");
      return;
    }
    
    const resource: ResourceItem = {
      id: generateId(),
      title: newResource.title,
      description: newResource.description || undefined,
      type: newResource.type as "file" | "link",
      url: newResource.url,
      file_name: newResource.file_name,
    };
    
    const newResources = [...resources, resource];
    updateSettingsMutation.mutate({ ...settings, resources: newResources });
    setNewResource({ type: "link", title: "", description: "", url: "" });
    setResourceDialogOpen(false);
    toast.success("Ressource ajoutée");
  };
  
  const handleUpdateResource = () => {
    if (!editingResource || !newResource.title || !newResource.url) {
      toast.error("Veuillez remplir le titre et l'URL");
      return;
    }
    
    const updatedResources = resources.map(r => 
      r.id === editingResource.id 
        ? {
            ...r,
            title: newResource.title!,
            description: newResource.description || undefined,
            type: newResource.type as "file" | "link",
            url: newResource.url!,
            file_name: newResource.file_name,
          }
        : r
    );
    
    updateSettingsMutation.mutate({ ...settings, resources: updatedResources });
    setEditingResource(null);
    setNewResource({ type: "link", title: "", description: "", url: "" });
    setResourceDialogOpen(false);
    toast.success("Ressource modifiée");
  };
  
  const handleDeleteResource = (id: string) => {
    const newResources = resources.filter(r => r.id !== id);
    updateSettingsMutation.mutate({ ...settings, resources: newResources });
    toast.success("Ressource supprimée");
  };
  
  const openEditResourceDialog = (resource: ResourceItem) => {
    setEditingResource(resource);
    setResourceInputMode(resource.type);
    setNewResource({
      type: resource.type,
      title: resource.title,
      description: resource.description || "",
      url: resource.url,
      file_name: resource.file_name,
    });
    setUploadedFileInfo(null);
    setResourceDialogOpen(true);
  };
  
  const openAddResourceDialog = () => {
    setEditingResource(null);
    setResourceInputMode("link");
    setNewResource({ type: "link", title: "", description: "", url: "" });
    setUploadedFileInfo(null);
    setResourceDialogOpen(true);
  };
  
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setIsUploading(true);
    try {
      const response = await uploadAttachment(versionId, file);
      setNewResource({
        ...newResource,
        type: "file",
        url: response.file_url,
        file_name: file.name,
        title: newResource.title || file.name.replace(/\.[^/.]+$/, ""), // Use filename without extension as default title
      });
      setUploadedFileInfo({
        name: file.name,
        size: file.size,
        type: file.type,
      });
      toast.success("Fichier uploadé");
    } catch (error) {
      toast.error("Erreur lors de l'upload", { description: getErrorMessage(error) });
    } finally {
      setIsUploading(false);
      // Reset input to allow re-uploading same file
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };
  
  const handleResourceInputModeChange = (mode: "link" | "upload") => {
    setResourceInputMode(mode);
    if (mode === "link") {
      setNewResource({ ...newResource, type: "link", url: "", file_name: undefined });
      setUploadedFileInfo(null);
    } else {
      setNewResource({ ...newResource, type: "file", url: "", file_name: undefined });
    }
  };

  // ============ GLOSSARY SETTINGS ============
  const enableGlossary = settings.enable_glossary ?? false;
  const glossaryTitle = settings.glossary_title ?? "";
  const glossaryTerms = settings.glossary_terms ?? [];
  
  // Local state for glossary editing
  const [localGlossaryTitle, setLocalGlossaryTitle] = useState(glossaryTitle);
  const [glossaryDialogOpen, setGlossaryDialogOpen] = useState(false);
  const [editingTerm, setEditingTerm] = useState<GlossaryTerm | null>(null);
  const [newTerm, setNewTerm] = useState<Partial<GlossaryTerm>>({
    term: "",
    definition: "",
  });
  
  const handleEnableGlossaryChange = (checked: boolean) => {
    updateSettingsMutation.mutate({ ...settings, enable_glossary: checked });
  };
  
  const handleGlossaryTitleBlur = () => {
    if (localGlossaryTitle !== glossaryTitle) {
      updateSettingsMutation.mutate({ ...settings, glossary_title: localGlossaryTitle || undefined });
    }
  };
  
  const handleAddTerm = () => {
    if (!newTerm.term || !newTerm.definition) {
      toast.error("Veuillez remplir le terme et la définition");
      return;
    }
    
    const term: GlossaryTerm = {
      id: generateId(),
      term: newTerm.term,
      definition: newTerm.definition,
    };
    
    // Insert in alphabetical order
    const newTerms = [...glossaryTerms, term].sort((a, b) => 
      a.term.localeCompare(b.term, 'fr', { sensitivity: 'base' })
    );
    
    updateSettingsMutation.mutate({ ...settings, glossary_terms: newTerms });
    setNewTerm({ term: "", definition: "" });
    setGlossaryDialogOpen(false);
    toast.success("Terme ajouté");
  };
  
  const handleUpdateTerm = () => {
    if (!editingTerm || !newTerm.term || !newTerm.definition) {
      toast.error("Veuillez remplir le terme et la définition");
      return;
    }
    
    const updatedTerms = glossaryTerms
      .map(t => 
        t.id === editingTerm.id 
          ? { ...t, term: newTerm.term!, definition: newTerm.definition! }
          : t
      )
      .sort((a, b) => a.term.localeCompare(b.term, 'fr', { sensitivity: 'base' }));
    
    updateSettingsMutation.mutate({ ...settings, glossary_terms: updatedTerms });
    setEditingTerm(null);
    setNewTerm({ term: "", definition: "" });
    setGlossaryDialogOpen(false);
    toast.success("Terme modifié");
  };
  
  const handleDeleteTerm = (id: string) => {
    const newTerms = glossaryTerms.filter(t => t.id !== id);
    updateSettingsMutation.mutate({ ...settings, glossary_terms: newTerms });
    toast.success("Terme supprimé");
  };
  
  const openEditTermDialog = (term: GlossaryTerm) => {
    setEditingTerm(term);
    setNewTerm({
      term: term.term,
      definition: term.definition,
    });
    setGlossaryDialogOpen(true);
  };
  
  const openAddTermDialog = () => {
    setEditingTerm(null);
    setNewTerm({ term: "", definition: "" });
    setGlossaryDialogOpen(true);
  };
  
  // ============ GROUP VALIDATION SETTINGS ============
  const groupValidation = settings.group_validation;
  const enableGroupValidation = !!groupValidation;
  const groupValidationMode = groupValidation?.mode ?? "at_least_one";
  const groupValidationGroups = groupValidation?.groups ?? [];
  const groupValidationMessage = groupValidation?.message ?? "";
  
  // Local state for group validation message
  const [localGroupValidationMessage, setLocalGroupValidationMessage] = useState(groupValidationMessage);
  
  // Flatten groups for selection (including children)
  const flattenGroups = (groups: FormFieldGroupWithFields[]): { slug: string; name: string; depth: number }[] => {
    const result: { slug: string; name: string; depth: number }[] = [];
    const traverse = (groups: FormFieldGroupWithFields[], depth: number) => {
      for (const group of groups) {
        // Only include groups that have validation_rules (with when_active)
        // or that have children - basically groups that can be "active"
        result.push({ slug: group.slug, name: group.name, depth });
        if (group.children.length > 0) {
          traverse(group.children, depth + 1);
        }
      }
    };
    traverse(groups, 0);
    return result;
  };
  
  const availableGroups = formFieldGroups ? flattenGroups(formFieldGroups) : [];
  
  const handleEnableGroupValidationChange = (checked: boolean) => {
    if (checked) {
      // Enable with default settings
      updateSettingsMutation.mutate({ 
        ...settings, 
        group_validation: {
          mode: "at_least_one",
          groups: [],
          message: undefined,
        }
      });
    } else {
      // Disable
      updateSettingsMutation.mutate({ ...settings, group_validation: undefined });
    }
  };
  
  const handleGroupValidationModeChange = (mode: string) => {
    if (!groupValidation) return;
    updateSettingsMutation.mutate({ 
      ...settings, 
      group_validation: {
        ...groupValidation,
        mode: mode as GroupValidationSettings["mode"],
      }
    });
  };
  
  const handleGroupValidationGroupToggle = (slug: string) => {
    if (!groupValidation) return;
    const newGroups = groupValidationGroups.includes(slug)
      ? groupValidationGroups.filter(g => g !== slug)
      : [...groupValidationGroups, slug];
    updateSettingsMutation.mutate({ 
      ...settings, 
      group_validation: {
        ...groupValidation,
        groups: newGroups,
      }
    });
  };
  
  const handleGroupValidationMessageBlur = () => {
    if (!groupValidation) return;
    if (localGroupValidationMessage !== groupValidationMessage) {
      updateSettingsMutation.mutate({ 
        ...settings, 
        group_validation: {
          ...groupValidation,
          message: localGroupValidationMessage || undefined,
        }
      });
    }
  };

  // ============ CROSS-FIELD WARNINGS SETTINGS ============
  const crossFieldWarnings = settings.cross_field_warnings ?? [];
  
  // Local state for cross-field warning dialog
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);
  const [editingWarning, setEditingWarning] = useState<CrossFieldWarningRule | null>(null);
  const [newWarning, setNewWarning] = useState<Partial<CrossFieldWarningRule>>({
    condition: "",
    message: "",
    display_mode: "inline",
    target_fields: [],
    trigger_fields: [],
    enabled: true,
    severity: "warning",
  });
  
  // Search states for field selectors
  const [targetFieldsOpen, setTargetFieldsOpen] = useState(false);
  const [triggerFieldsOpen, setTriggerFieldsOpen] = useState(false);
  const [targetFieldsSearch, setTargetFieldsSearch] = useState("");
  const [triggerFieldsSearch, setTriggerFieldsSearch] = useState("");
  
  // Flatten all form fields from groups for selection
  const flattenFields = (groups: FormFieldGroupWithFields[]): FormField[] => {
    const result: FormField[] = [];
    const traverse = (groups: FormFieldGroupWithFields[]) => {
      for (const group of groups) {
        result.push(...group.fields);
        if (group.children.length > 0) {
          traverse(group.children);
        }
      }
    };
    traverse(groups);
    return result;
  };
  
  const availableFields = formFieldGroups ? flattenFields(formFieldGroups) : [];
  
  // Filter fields based on search
  const filteredTargetFields = availableFields.filter(field => 
    targetFieldsSearch === "" ||
    field.label.toLowerCase().includes(targetFieldsSearch.toLowerCase()) ||
    field.slug.toLowerCase().includes(targetFieldsSearch.toLowerCase())
  );
  
  const filteredTriggerFields = availableFields.filter(field => 
    triggerFieldsSearch === "" ||
    field.label.toLowerCase().includes(triggerFieldsSearch.toLowerCase()) ||
    field.slug.toLowerCase().includes(triggerFieldsSearch.toLowerCase())
  );
  
  const handleAddWarning = () => {
    const isBlockingError = newWarning.severity === "error";
    const isInlineMode = isBlockingError || newWarning.display_mode !== "results_only";
    
    if (!newWarning.condition || !newWarning.message) {
      toast.error("Veuillez remplir la condition et le message");
      return;
    }
    
    if (isInlineMode && (!newWarning.target_fields?.length || !newWarning.trigger_fields?.length)) {
      toast.error("Veuillez sélectionner les champs d'affichage et de déclenchement");
      return;
    }
    
    const warning: CrossFieldWarningRule = {
      id: generateId(),
      condition: newWarning.condition,
      message: newWarning.message,
      display_mode: isBlockingError ? "inline" : (newWarning.display_mode ?? "inline"),
      target_fields: isInlineMode ? newWarning.target_fields : [],
      trigger_fields: isInlineMode ? newWarning.trigger_fields : [],
      enabled: newWarning.enabled ?? true,
      severity: newWarning.severity ?? "warning",
    };
    
    const newWarnings = [...crossFieldWarnings, warning];
    updateSettingsMutation.mutate({ ...settings, cross_field_warnings: newWarnings });
    setNewWarning({ condition: "", message: "", display_mode: "inline", target_fields: [], trigger_fields: [], enabled: true, severity: "warning" });
    setWarningDialogOpen(false);
    toast.success(isBlockingError ? "Règle de validation bloquante ajoutée" : "Règle d'avertissement ajoutée");
  };
  
  const handleUpdateWarning = () => {
    const isBlockingError = newWarning.severity === "error";
    const isInlineMode = isBlockingError || newWarning.display_mode !== "results_only";
    
    if (!editingWarning || !newWarning.condition || !newWarning.message) {
      toast.error("Veuillez remplir la condition et le message");
      return;
    }
    
    if (isInlineMode && (!newWarning.target_fields?.length || !newWarning.trigger_fields?.length)) {
      toast.error("Veuillez sélectionner les champs d'affichage et de déclenchement");
      return;
    }
    
    const updatedWarnings = crossFieldWarnings.map(w => 
      w.id === editingWarning.id 
        ? {
            ...w,
            condition: newWarning.condition!,
            message: newWarning.message!,
            display_mode: isBlockingError ? "inline" as WarningDisplayMode : (newWarning.display_mode ?? "inline"),
            target_fields: isInlineMode ? newWarning.target_fields! : [],
            trigger_fields: isInlineMode ? newWarning.trigger_fields! : [],
            enabled: newWarning.enabled ?? true,
            severity: newWarning.severity ?? "warning",
          }
        : w
    );
    
    updateSettingsMutation.mutate({ ...settings, cross_field_warnings: updatedWarnings });
    setEditingWarning(null);
    setNewWarning({ condition: "", message: "", display_mode: "inline", target_fields: [], trigger_fields: [], enabled: true, severity: "warning" });
    setWarningDialogOpen(false);
    toast.success("Règle modifiée");
  };
  
  const handleDeleteWarning = (id: string) => {
    const newWarnings = crossFieldWarnings.filter(w => w.id !== id);
    updateSettingsMutation.mutate({ ...settings, cross_field_warnings: newWarnings });
    toast.success("Règle supprimée");
  };
  
  const handleToggleWarningEnabled = (id: string) => {
    const updatedWarnings = crossFieldWarnings.map(w => 
      w.id === id ? { ...w, enabled: !w.enabled } : w
    );
    updateSettingsMutation.mutate({ ...settings, cross_field_warnings: updatedWarnings });
  };
  
  const openEditWarningDialog = (warning: CrossFieldWarningRule) => {
    setEditingWarning(warning);
    setNewWarning({
      condition: warning.condition,
      message: warning.message,
      display_mode: warning.display_mode ?? "inline",
      target_fields: warning.target_fields ?? [],
      trigger_fields: warning.trigger_fields ?? [],
      enabled: warning.enabled ?? true,
      severity: warning.severity ?? "warning",
    });
    setWarningDialogOpen(true);
  };
  
  const openAddWarningDialog = () => {
    setEditingWarning(null);
    setNewWarning({ condition: "", message: "", display_mode: "inline", target_fields: [], trigger_fields: [], enabled: true, severity: "warning" });
    setTargetFieldsSearch("");
    setTriggerFieldsSearch("");
    setWarningDialogOpen(true);
  };
  
  const handleTargetFieldToggle = (slug: string) => {
    const currentFields = newWarning.target_fields || [];
    const newFields = currentFields.includes(slug)
      ? currentFields.filter(f => f !== slug)
      : [...currentFields, slug];
    setNewWarning({ ...newWarning, target_fields: newFields });
  };
  
  const handleTriggerFieldToggle = (slug: string) => {
    const currentFields = newWarning.trigger_fields || [];
    const newFields = currentFields.includes(slug)
      ? currentFields.filter(f => f !== slug)
      : [...currentFields, slug];
    setNewWarning({ ...newWarning, trigger_fields: newFields });
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Paramètres du simulateur
          </CardTitle>
          <CardDescription>
            Configurez le comportement général du simulateur.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Auto Calculate Setting */}
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-lg border">
              <div className="flex items-start gap-4">
                <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                  autoCalculate ? "bg-success/10" : "bg-info/10"
                }`}>
                  {autoCalculate ? (
                    <Zap className="h-5 w-5 text-success" />
                  ) : (
                    <MousePointerClick className="h-5 w-5 text-info" />
                  )}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="auto-calculate" className="text-base font-medium cursor-pointer">
                    Calcul automatique
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {autoCalculate
                      ? "Les résultats se calculent automatiquement quand l'utilisateur modifie les champs d'entrée (mode Excel)."
                      : "L'utilisateur doit cliquer sur le bouton « Calculer » pour lancer le calcul."}
                  </p>
                </div>
              </div>
              <Switch
                id="auto-calculate"
                checked={autoCalculate}
                onCheckedChange={handleAutoCalculateChange}
                disabled={!isEditable || updateSettingsMutation.isPending}
              />
            </div>

            {/* Mode Comparison */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Manual Mode */}
              <div className={`p-4 rounded-lg border-2 transition-colors ${
                !autoCalculate ? "border-primary bg-primary/5" : "border-muted"
              }`}>
                <div className="flex items-center gap-2 mb-3">
                  <MousePointerClick className={`h-4 w-4 ${!autoCalculate ? "text-primary" : "text-muted-foreground"}`} />
                  <span className={`font-medium ${!autoCalculate ? "text-primary" : "text-muted-foreground"}`}>
                    Mode manuel
                  </span>
                  {!autoCalculate && (
                    <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
                      Actif
                    </span>
                  )}
                </div>
                <ul className="text-sm text-muted-foreground space-y-1.5">
                  <li className="flex items-start gap-2">
                    <span className="text-muted-foreground/60">•</span>
                    <span>Bouton « Calculer » visible</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-muted-foreground/60">•</span>
                    <span>L&apos;utilisateur contrôle quand lancer le calcul</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-muted-foreground/60">•</span>
                    <span>Idéal pour les formulaires complexes</span>
                  </li>
                </ul>
              </div>

              {/* Auto Mode */}
              <div className={`p-4 rounded-lg border-2 transition-colors ${
                autoCalculate ? "border-primary bg-primary/5" : "border-muted"
              }`}>
                <div className="flex items-center gap-2 mb-3">
                  <Zap className={`h-4 w-4 ${autoCalculate ? "text-primary" : "text-muted-foreground"}`} />
                  <span className={`font-medium ${autoCalculate ? "text-primary" : "text-muted-foreground"}`}>
                    Mode automatique
                  </span>
                  {autoCalculate && (
                    <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
                      Actif
                    </span>
                  )}
                </div>
                <ul className="text-sm text-muted-foreground space-y-1.5">
                  <li className="flex items-start gap-2">
                    <span className="text-muted-foreground/60">•</span>
                    <span>Résultats calculés en temps réel</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-muted-foreground/60">•</span>
                    <span>Comportement similaire à Excel</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-muted-foreground/60">•</span>
                    <span>Idéal pour les simulateurs simples</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Info box */}
            <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/50 text-sm">
              <Info className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div className="text-muted-foreground">
                <p>
                  <strong>Note :</strong> En mode automatique, un délai de 500ms est appliqué
                  après chaque modification pour éviter les calculs excessifs pendant la saisie.
                  Les cotations sont toujours enregistrées dans l&apos;historique.
                </p>
              </div>
            </div>

            {/* Auto-save Draft Setting (only visible in manual mode) */}
            {!autoCalculate && (
              <div className="space-y-4 pl-4 border-l-2 border-primary/30 ml-5">
                <div className="flex items-center justify-between p-4 rounded-lg border bg-primary/5">
                  <div className="flex items-start gap-4">
                    <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                      autoSaveDraft ? "bg-success/10" : "bg-muted"
                    }`}>
                      <Save className={`h-5 w-5 ${autoSaveDraft ? "text-success" : "text-muted-foreground"}`} />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="auto-save-draft" className="text-base font-medium cursor-pointer">
                        Sauvegarde automatique du brouillon
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        {autoSaveDraft
                          ? "Les données saisies sont automatiquement enregistrées en brouillon dans la base de données."
                          : "Les données ne sont pas sauvegardées automatiquement. L'utilisateur doit terminer sa cotation en une seule session."}
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="auto-save-draft"
                    checked={autoSaveDraft}
                    onCheckedChange={handleAutoSaveDraftChange}
                    disabled={!isEditable || updateSettingsMutation.isPending}
                  />
                </div>

                {autoSaveDraft && (
                  <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/50 text-sm">
                    <Info className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                    <div className="text-muted-foreground">
                      <p>
                        <strong>Fonctionnement :</strong> Le brouillon est enregistré en base de données
                         après chaque modification (avec un délai d&apos;1 seconde).
                        Il sera automatiquement restauré lors de la prochaine visite.
                        Le brouillon est supprimé après l&apos;exécution d&apos;une cotation ou la réinitialisation du formulaire.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Notes Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Notes de cotation
          </CardTitle>
          <CardDescription>
            Permettez aux utilisateurs d&apos;ajouter des notes à leurs cotations.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 rounded-lg border">
            <div className="flex items-start gap-4">
              <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                enableNotes ? "bg-success/10" : "bg-muted"
              }`}>
                <FileText className={`h-5 w-5 ${enableNotes ? "text-success" : "text-muted-foreground"}`} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="enable-notes" className="text-base font-medium cursor-pointer">
                  Activer les notes
                </Label>
                <p className="text-sm text-muted-foreground">
                  {enableNotes
                    ? "Les utilisateurs peuvent ajouter des notes personnelles à leurs cotations."
                    : "Le champ notes est masqué dans le formulaire de cotation."}
                </p>
              </div>
            </div>
            <Switch
              id="enable-notes"
              checked={enableNotes}
              onCheckedChange={handleEnableNotesChange}
              disabled={!isEditable || updateSettingsMutation.isPending}
            />
          </div>
        </CardContent>
      </Card>
      
      {/* Output Display Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Affichage des résultats
          </CardTitle>
          <CardDescription>
            Configurez l&apos;affichage des champs de calcul dans les résultats.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Hide Zero Outputs Toggle */}
          <div className="flex items-center justify-between p-4 rounded-lg border">
            <div className="flex items-start gap-4">
              <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                hideZeroOutputs ? "bg-success/10" : "bg-muted"
              }`}>
                <EyeOff className={`h-5 w-5 ${hideZeroOutputs ? "text-success" : "text-muted-foreground"}`} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="hide-zero-outputs" className="text-base font-medium cursor-pointer">
                  Cacher les valeurs à zéro
                </Label>
                <p className="text-sm text-muted-foreground">
                  {hideZeroOutputs
                    ? "Les champs de calcul dont la valeur est 0 sont masqués dans les résultats."
                    : "Tous les champs de calcul sont affichés, même ceux à 0."}
                </p>
              </div>
            </div>
            <Switch
              id="hide-zero-outputs"
              checked={hideZeroOutputs}
              onCheckedChange={handleHideZeroOutputsChange}
              disabled={!isEditable || updateSettingsMutation.isPending}
            />
          </div>
          
          {/* Show All Outputs Button Toggle */}
          <div className="flex items-center justify-between p-4 rounded-lg border">
            <div className="flex items-start gap-4">
              <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                showAllOutputsButton ? "bg-success/10" : "bg-muted"
              }`}>
                <Eye className={`h-5 w-5 ${showAllOutputsButton ? "text-success" : "text-muted-foreground"}`} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="show-all-outputs-button" className="text-base font-medium cursor-pointer">
                  Afficher le bouton &quot;Afficher tout&quot;
                </Label>
                <p className="text-sm text-muted-foreground">
                  {showAllOutputsButton
                    ? "Un bouton permet à l'utilisateur d'afficher tous les champs, y compris ceux masqués (valeurs à zéro ou conditions de visibilité)."
                    : "L'utilisateur ne peut pas afficher les champs masqués."}
                </p>
              </div>
            </div>
            <Switch
              id="show-all-outputs-button"
              checked={showAllOutputsButton}
              onCheckedChange={handleShowAllOutputsButtonChange}
              disabled={!isEditable || updateSettingsMutation.isPending}
            />
          </div>
        </CardContent>
      </Card>
      
      {/* Proposal Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Propositions commerciales
          </CardTitle>
          <CardDescription>
            Configurez le comportement lors de la génération des propositions commerciales.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between p-4 rounded-lg border">
            <div className="flex items-start gap-4">
              <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                hideZeroTableRowsInProposals ? "bg-success/10" : "bg-muted"
              }`}>
                <EyeOff className={`h-5 w-5 ${hideZeroTableRowsInProposals ? "text-success" : "text-muted-foreground"}`} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="hide-zero-table-rows-in-proposals" className="text-base font-medium cursor-pointer">
                  Masquer les lignes à zéro dans les tableaux
                </Label>
                <p className="text-sm text-muted-foreground">
                  {hideZeroTableRowsInProposals
                    ? "Les lignes de tableaux dont toutes les valeurs dynamiques sont à 0 sont masquées lors de la création d'une proposition."
                    : "Toutes les lignes de tableaux sont affichées dans les propositions, même celles à 0."}
                </p>
              </div>
            </div>
            <Switch
              id="hide-zero-table-rows-in-proposals"
              checked={hideZeroTableRowsInProposals}
              onCheckedChange={handleHideZeroTableRowsInProposalsChange}
              disabled={!isEditable || updateSettingsMutation.isPending}
            />
          </div>
        </CardContent>
      </Card>
      
      {/* Group Validation Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckSquare className="h-5 w-5" />
            Validation des groupes de champs
          </CardTitle>
          <CardDescription>
            Exigez qu&apos;au moins un groupe de champs soit rempli avant de pouvoir calculer.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable Group Validation Toggle */}
          <div className="flex items-center justify-between p-4 rounded-lg border">
            <div className="flex items-start gap-4">
              <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                enableGroupValidation ? "bg-success/10" : "bg-muted"
              }`}>
                <CheckSquare className={`h-5 w-5 ${enableGroupValidation ? "text-success" : "text-muted-foreground"}`} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="enable-group-validation" className="text-base font-medium cursor-pointer">
                  Activer la validation des groupes
                </Label>
                <p className="text-sm text-muted-foreground">
                  {enableGroupValidation
                    ? "L'utilisateur doit remplir au moins un groupe avant de pouvoir lancer le calcul."
                    : "Aucune validation globale des groupes n'est effectuée."}
                </p>
              </div>
            </div>
            <Switch
              id="enable-group-validation"
              checked={enableGroupValidation}
              onCheckedChange={handleEnableGroupValidationChange}
              disabled={!isEditable || updateSettingsMutation.isPending}
            />
          </div>
          
          {/* Group Validation configuration (only shown when enabled) */}
          {enableGroupValidation && (
            <div className="space-y-6 pl-4 border-l-2 border-muted ml-5">
              {/* Validation Mode */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Mode de validation</Label>
                <Select
                  value={groupValidationMode}
                  onValueChange={handleGroupValidationModeChange}
                  disabled={!isEditable || updateSettingsMutation.isPending}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="at_least_one">Au moins un groupe actif</SelectItem>
                    <SelectItem value="all_required">Tous les groupes requis</SelectItem>
                    <SelectItem value="any">Aucune contrainte (désactivé)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {groupValidationMode === "at_least_one" && "L'utilisateur doit remplir au moins un des groupes sélectionnés."}
                  {groupValidationMode === "all_required" && "L'utilisateur doit remplir tous les groupes sélectionnés."}
                  {groupValidationMode === "any" && "Aucune validation n'est effectuée sur les groupes."}
                </p>
              </div>
              
              <Separator />
              
              {/* Groups Selection */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">
                  Groupes concernés ({groupValidationGroups.length} sélectionné{groupValidationGroups.length > 1 ? "s" : ""})
                </Label>
                
                {availableGroups.length === 0 ? (
                  <div className="text-center py-4 border rounded-lg border-dashed">
                    <p className="text-sm text-muted-foreground">
                      Aucun groupe de champs disponible
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                    {availableGroups.map((group) => {
                      const isSelected = groupValidationGroups.includes(group.slug);
                      return (
                        <button
                          key={group.slug}
                          type="button"
                          onClick={() => handleGroupValidationGroupToggle(group.slug)}
                          disabled={!isEditable || updateSettingsMutation.isPending}
                          className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ${
                            isSelected
                              ? "border-primary bg-primary/5"
                              : "border-muted hover:border-muted-foreground/50"
                          } ${!isEditable ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                        >
                          <div className={`h-5 w-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                            isSelected ? "border-primary bg-primary" : "border-muted-foreground/30"
                          }`}>
                            {isSelected && (
                              <svg className="h-3 w-3 text-primary-foreground" viewBox="0 0 12 12" fill="none">
                                <path d="M2 6l3 3 5-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            )}
                          </div>
                          <span 
                            className={`text-sm ${isSelected ? "font-medium" : ""}`}
                            style={{ paddingLeft: `${group.depth * 12}px` }}
                          >
                            {group.depth > 0 && "↳ "}
                            {group.name}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
                
                <p className="text-xs text-muted-foreground">
                  Sélectionnez les groupes de champs qui doivent être validés. 
                  Un groupe est considéré comme « actif » lorsqu&apos;au moins un de ses champs est rempli.
                </p>
              </div>
              
              <Separator />
              
              {/* Custom Error Message */}
              <div className="space-y-2">
                <Label htmlFor="group-validation-message" className="text-sm font-medium">
                  Message d&apos;erreur personnalisé (optionnel)
                </Label>
                <Input
                  id="group-validation-message"
                  placeholder="Veuillez remplir au moins un groupe de champs"
                  value={localGroupValidationMessage}
                  onChange={(e) => setLocalGroupValidationMessage(e.target.value)}
                  onBlur={handleGroupValidationMessageBlur}
                  disabled={!isEditable || updateSettingsMutation.isPending}
                />
                <p className="text-xs text-muted-foreground">
                  Message affiché si la validation échoue. Laissez vide pour le message par défaut.
                </p>
              </div>
              
              {/* Info box */}
              <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/50 text-sm">
                <Info className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div className="text-muted-foreground">
                  <p>
                    <strong>Exemple d&apos;usage :</strong> Si vous avez un simulateur avec des groupes « Colis » et « Palette », 
                    vous pouvez exiger qu&apos;au moins un de ces groupes soit rempli pour effectuer le calcul. 
                    L&apos;utilisateur ne pourra pas calculer s&apos;il n&apos;a renseigné aucune donnée dans ces groupes.
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Attachments Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Paperclip className="h-5 w-5" />
            Pièces jointes
          </CardTitle>
          <CardDescription>
            Permettez aux utilisateurs d&apos;attacher des fichiers à leurs cotations.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable Attachments Toggle */}
          <div className="flex items-center justify-between p-4 rounded-lg border">
            <div className="flex items-start gap-4">
              <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                enableAttachments ? "bg-success/10" : "bg-muted"
              }`}>
                <Paperclip className={`h-5 w-5 ${enableAttachments ? "text-success" : "text-muted-foreground"}`} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="enable-attachments" className="text-base font-medium cursor-pointer">
                  Activer les pièces jointes
                </Label>
                <p className="text-sm text-muted-foreground">
                  {enableAttachments
                    ? "Les utilisateurs peuvent ajouter des fichiers (PDF, documents, images, etc.) à leurs cotations."
                    : "Les pièces jointes sont désactivées pour ce simulateur."}
                </p>
              </div>
            </div>
            <Switch
              id="enable-attachments"
              checked={enableAttachments}
              onCheckedChange={handleEnableAttachmentsChange}
              disabled={!isEditable || updateSettingsMutation.isPending}
            />
          </div>
          
          {/* Attachments configuration (only shown when enabled) */}
          {enableAttachments && (
            <div className="space-y-6 pl-4 border-l-2 border-muted ml-5">
              {/* Max Attachments */}
              <div className="space-y-3">
                <Label htmlFor="max-attachments" className="text-sm font-medium">
                  Nombre maximum de fichiers
                </Label>
                <div className="flex items-center gap-3">
                  <Input
                    id="max-attachments"
                    type="number"
                    min="0"
                    value={maxAttachmentsInput}
                    onChange={(e) => handleMaxAttachmentsChange(e.target.value)}
                    disabled={!isEditable || updateSettingsMutation.isPending}
                    className="w-24"
                  />
                  <span className="text-sm text-muted-foreground">
                    {maxAttachments === 0 ? "(illimité)" : `fichier${maxAttachments > 1 ? "s" : ""} maximum`}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Entrez 0 pour ne pas limiter le nombre de fichiers.
                </p>
              </div>
              
              <Separator />
              
              {/* Allowed File Types */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Types de fichiers autorisés</Label>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSelectAllFileTypes}
                      disabled={!isEditable || updateSettingsMutation.isPending}
                    >
                      Tout sélectionner
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleClearFileTypes}
                      disabled={!isEditable || updateSettingsMutation.isPending}
                    >
                      Tout autoriser
                    </Button>
                  </div>
                </div>
                
                {allowedFileTypes.length === 0 ? (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-success/10 text-success text-sm">
                    <Info className="h-4 w-4" />
                    <span>Tous les types de fichiers courants sont autorisés</span>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {allowedFileTypes.map(type => (
                      <Badge key={type} variant="secondary" className="gap-1">
                        {type.toUpperCase()}
                        {isEditable && (
                          <button
                            onClick={() => handleFileTypeToggle(type)}
                            className="ml-1 hover:text-destructive"
                            disabled={updateSettingsMutation.isPending}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        )}
                      </Badge>
                    ))}
                  </div>
                )}
                
                {/* File type selector by category */}
                <div className="space-y-4">
                  {["Documents", "Images", "Archives"].map(category => (
                    <div key={category} className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        {category}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {FILE_TYPE_OPTIONS.filter(f => f.category === category).map(fileType => {
                          const isSelected = allowedFileTypes.includes(fileType.value);
                          const isAllAllowed = allowedFileTypes.length === 0;
                          
                          return (
                            <button
                              key={fileType.value}
                              onClick={() => handleFileTypeToggle(fileType.value)}
                              disabled={!isEditable || updateSettingsMutation.isPending}
                              className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                                isSelected
                                  ? "bg-primary text-primary-foreground border-primary"
                                  : isAllAllowed
                                    ? "bg-muted/50 text-muted-foreground border-muted hover:border-primary/50"
                                    : "bg-background text-muted-foreground border-muted hover:border-primary/50"
                              } ${!isEditable ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                            >
                              {fileType.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                
                <p className="text-xs text-muted-foreground">
                  Sélectionnez les types de fichiers que les utilisateurs peuvent télécharger. 
                  Si aucun n&apos;est sélectionné, tous les types courants sont autorisés.
                </p>
              </div>
              
              {/* Info box */}
              <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/50 text-sm">
                <Info className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div className="text-muted-foreground">
                  <p>
                    <strong>Note :</strong> La taille maximale par fichier est de 50 Mo. 
                    Les pièces jointes sont stockées de manière sécurisée et accessibles uniquement 
                    par les utilisateurs autorisés. L&apos;emplacement de la section peut être configuré 
                    dans l&apos;onglet &quot;Apparence&quot;.
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Foreword Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Avant-propos
          </CardTitle>
          <CardDescription>
            Ajoutez un texte d&apos;introduction affiché en haut du simulateur.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable Foreword Toggle */}
          <div className="flex items-center justify-between p-4 rounded-lg border">
            <div className="flex items-start gap-4">
              <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                enableForeword ? "bg-success/10" : "bg-muted"
              }`}>
                <BookOpen className={`h-5 w-5 ${enableForeword ? "text-success" : "text-muted-foreground"}`} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="enable-foreword" className="text-base font-medium cursor-pointer">
                  Activer l&apos;avant-propos
                </Label>
                <p className="text-sm text-muted-foreground">
                  {enableForeword
                    ? "Un encadré d'introduction est affiché au-dessus du formulaire de cotation."
                    : "Aucun avant-propos n'est affiché dans le simulateur."}
                </p>
              </div>
            </div>
            <Switch
              id="enable-foreword"
              checked={enableForeword}
              onCheckedChange={handleEnableForewordChange}
              disabled={!isEditable || updateSettingsMutation.isPending}
            />
          </div>
          
          {/* Foreword configuration (only shown when enabled) */}
          {enableForeword && (
            <div className="space-y-4 pl-4 border-l-2 border-muted ml-5">
              {/* Foreword Title */}
              <div className="space-y-2">
                <Label htmlFor="foreword-title" className="text-sm font-medium">
                  Titre (optionnel)
                </Label>
                <Input
                  id="foreword-title"
                  placeholder="Avant-propos"
                  value={localForewordTitle}
                  onChange={(e) => setLocalForewordTitle(e.target.value)}
                  onBlur={handleForewordTitleBlur}
                  disabled={!isEditable || updateSettingsMutation.isPending}
                />
                <p className="text-xs text-muted-foreground">
                  Laissez vide pour utiliser le titre par défaut « Avant-propos ».
                </p>
              </div>
              
              {/* Foreword Content */}
              <div className="space-y-2">
                <Label htmlFor="foreword-content" className="text-sm font-medium">
                  Contenu
                </Label>
                <Textarea
                  id="foreword-content"
                  placeholder="Entrez le texte d'introduction du simulateur..."
                  value={localForewordContent}
                  onChange={(e) => setLocalForewordContent(e.target.value)}
                  onBlur={handleForewordContentBlur}
                  disabled={!isEditable || updateSettingsMutation.isPending}
                  rows={5}
                />
                <p className="text-xs text-muted-foreground">
                  Ce texte sera affiché dans un encadré en haut du simulateur.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Resources Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Library className="h-5 w-5" />
            Bibliothèque de ressources
          </CardTitle>
          <CardDescription>
            Ajoutez des documents téléchargeables (PDF, guides, etc.) ou des liens utiles.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable Resources Toggle */}
          <div className="flex items-center justify-between p-4 rounded-lg border">
            <div className="flex items-start gap-4">
              <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                enableResources ? "bg-success/10" : "bg-muted"
              }`}>
                <Library className={`h-5 w-5 ${enableResources ? "text-success" : "text-muted-foreground"}`} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="enable-resources" className="text-base font-medium cursor-pointer">
                  Activer la bibliothèque de ressources
                </Label>
                <p className="text-sm text-muted-foreground">
                  {enableResources
                    ? "Une section de ressources téléchargeables est affichée dans le simulateur."
                    : "Aucune bibliothèque de ressources n'est affichée."}
                </p>
              </div>
            </div>
            <Switch
              id="enable-resources"
              checked={enableResources}
              onCheckedChange={handleEnableResourcesChange}
              disabled={!isEditable || updateSettingsMutation.isPending}
            />
          </div>
          
          {/* Resources configuration (only shown when enabled) */}
          {enableResources && (
            <div className="space-y-4 pl-4 border-l-2 border-muted ml-5">
              {/* Resources Title */}
              <div className="space-y-2">
                <Label htmlFor="resources-title" className="text-sm font-medium">
                  Titre de la section (optionnel)
                </Label>
                <Input
                  id="resources-title"
                  placeholder="Ressources"
                  value={localResourcesTitle}
                  onChange={(e) => setLocalResourcesTitle(e.target.value)}
                  onBlur={handleResourcesTitleBlur}
                  disabled={!isEditable || updateSettingsMutation.isPending}
                />
              </div>
              
              <Separator />
              
              {/* Resources List */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">
                    Ressources ({resources.length})
                  </Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={openAddResourceDialog}
                    disabled={!isEditable || updateSettingsMutation.isPending}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Ajouter
                  </Button>
                </div>
                
                {resources.length === 0 ? (
                  <div className="text-center py-6 border rounded-lg border-dashed">
                    <Library className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
                    <p className="text-sm text-muted-foreground">
                      Aucune ressource ajoutée
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Cliquez sur « Ajouter » pour créer une ressource
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {resources.map((resource) => (
                      <div
                        key={resource.id}
                        className="flex items-center gap-3 p-3 border rounded-lg bg-muted/30"
                      >
                        <div className={`h-8 w-8 rounded flex items-center justify-center flex-shrink-0 ${
                          resource.type === "link" ? "bg-info/10" : "bg-warning/10"
                        }`}>
                          {resource.type === "link" ? (
                            <ExternalLink className="h-4 w-4 text-info" />
                          ) : (
                            <FileText className="h-4 w-4 text-warning" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{resource.title}</p>
                          {resource.description && (
                            <p className="text-xs text-muted-foreground truncate">
                              {resource.description}
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground/70 truncate">
                            {resource.url}
                          </p>
                        </div>
                        {isEditable && (
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => openEditResourceDialog(resource)}
                              disabled={updateSettingsMutation.isPending}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => handleDeleteResource(resource.id)}
                              disabled={updateSettingsMutation.isPending}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Glossary Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Book className="h-5 w-5" />
            Glossaire
          </CardTitle>
          <CardDescription>
            Définissez les termes techniques utilisés dans le simulateur.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable Glossary Toggle */}
          <div className="flex items-center justify-between p-4 rounded-lg border">
            <div className="flex items-start gap-4">
              <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                enableGlossary ? "bg-success/10" : "bg-muted"
              }`}>
                <Book className={`h-5 w-5 ${enableGlossary ? "text-success" : "text-muted-foreground"}`} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="enable-glossary" className="text-base font-medium cursor-pointer">
                  Activer le glossaire
                </Label>
                <p className="text-sm text-muted-foreground">
                  {enableGlossary
                    ? "Un bouton « Glossaire » permet d'accéder aux définitions des termes."
                    : "Le glossaire est désactivé pour ce simulateur."}
                </p>
              </div>
            </div>
            <Switch
              id="enable-glossary"
              checked={enableGlossary}
              onCheckedChange={handleEnableGlossaryChange}
              disabled={!isEditable || updateSettingsMutation.isPending}
            />
          </div>
          
          {/* Glossary configuration (only shown when enabled) */}
          {enableGlossary && (
            <div className="space-y-4 pl-4 border-l-2 border-muted ml-5">
              {/* Glossary Title */}
              <div className="space-y-2">
                <Label htmlFor="glossary-title" className="text-sm font-medium">
                  Titre (optionnel)
                </Label>
                <Input
                  id="glossary-title"
                  placeholder="Glossaire"
                  value={localGlossaryTitle}
                  onChange={(e) => setLocalGlossaryTitle(e.target.value)}
                  onBlur={handleGlossaryTitleBlur}
                  disabled={!isEditable || updateSettingsMutation.isPending}
                />
              </div>
              
              <Separator />
              
              {/* Glossary Terms List */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">
                    Termes ({glossaryTerms.length})
                  </Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={openAddTermDialog}
                    disabled={!isEditable || updateSettingsMutation.isPending}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Ajouter
                  </Button>
                </div>
                
                {glossaryTerms.length === 0 ? (
                  <div className="text-center py-6 border rounded-lg border-dashed">
                    <Book className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
                    <p className="text-sm text-muted-foreground">
                      Aucun terme défini
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Cliquez sur « Ajouter » pour définir un terme
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                    {glossaryTerms.map((term) => (
                      <div
                        key={term.id}
                        className="p-3 border rounded-lg bg-muted/30"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm">{term.term}</p>
                            <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">
                              {term.definition}
                            </p>
                          </div>
                          {isEditable && (
                            <div className="flex gap-1 flex-shrink-0">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => openEditTermDialog(term)}
                                disabled={updateSettingsMutation.isPending}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive hover:text-destructive"
                                onClick={() => handleDeleteTerm(term.id)}
                                disabled={updateSettingsMutation.isPending}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Info box */}
              <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/50 text-sm">
                <Info className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div className="text-muted-foreground">
                  <p>
                    Les termes sont automatiquement triés par ordre alphabétique. 
                    L&apos;utilisateur pourra accéder au glossaire via un bouton dans l&apos;en-tête du simulateur.
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Cross-Field Warnings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Avertissements multi-champs
          </CardTitle>
          <CardDescription>
            Configurez des règles d&apos;avertissement basées sur plusieurs champs de formulaire.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add Rule Button */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {crossFieldWarnings.length === 0 
                ? "Aucune règle d'avertissement configurée."
                : `${crossFieldWarnings.length} règle(s) configurée(s)`}
            </p>
            {isEditable && (
              <Button
                variant="outline"
                size="sm"
                onClick={openAddWarningDialog}
                disabled={updateSettingsMutation.isPending || availableFields.length === 0}
              >
                <Plus className="h-4 w-4 mr-2" />
                Ajouter une règle
              </Button>
            )}
          </div>
          
          {availableFields.length === 0 && (
            <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/50 text-sm">
              <Info className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div className="text-muted-foreground">
                <p>Créez d&apos;abord des champs de formulaire pour pouvoir configurer des règles d&apos;avertissement.</p>
              </div>
            </div>
          )}
          
          {/* Rules List */}
          {crossFieldWarnings.length > 0 && (
            <div className="space-y-3">
              {crossFieldWarnings.map((warning) => (
                <div
                  key={warning.id}
                  className={`p-4 rounded-lg border ${
                    warning.enabled === false 
                      ? 'bg-muted/30 border-muted' 
                      : warning.severity === "error" 
                        ? 'bg-destructive/5 border-destructive/30' 
                        : 'bg-yellow-500/5 border-yellow-500/30'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className={`h-4 w-4 flex-shrink-0 ${
                          warning.enabled === false 
                            ? 'text-muted-foreground' 
                            : warning.severity === "error" 
                              ? 'text-destructive' 
                              : 'text-yellow-500'
                        }`} />
                        <span className={`font-medium ${warning.enabled !== false ? '' : 'text-muted-foreground'}`}>
                          {warning.message}
                        </span>
                        {warning.severity === "error" && warning.enabled !== false && (
                          <Badge variant="destructive" className="text-xs">Bloquant</Badge>
                        )}
                        {warning.enabled === false && (
                          <Badge variant="secondary" className="text-xs">Désactivé</Badge>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground space-y-1">
                        <p>
                          <span className="font-medium">Condition:</span>{" "}
                          <code className="px-1.5 py-0.5 rounded bg-muted text-xs">{warning.condition}</code>
                        </p>
                        <p>
                          <span className="font-medium">Affichage:</span>{" "}
                          {warning.display_mode === "results_only" 
                            ? <Badge variant="outline" className="text-xs">Après calcul uniquement</Badge>
                            : <Badge variant="outline" className="text-xs">Sous les champs</Badge>
                          }
                        </p>
                        {warning.display_mode !== "results_only" && warning.target_fields && warning.target_fields.length > 0 && (
                          <p>
                            <span className="font-medium">Affiché sous:</span>{" "}
                            {warning.target_fields.map(slug => 
                              availableFields.find(f => f.slug === slug)?.label || slug
                            ).join(", ")}
                          </p>
                        )}
                        {warning.display_mode !== "results_only" && warning.trigger_fields && warning.trigger_fields.length > 0 && (
                          <p>
                            <span className="font-medium">Déclenché par:</span>{" "}
                            {warning.trigger_fields.map(slug => 
                              availableFields.find(f => f.slug === slug)?.label || slug
                            ).join(", ")}
                          </p>
                        )}
                      </div>
                    </div>
                    {isEditable && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleToggleWarningEnabled(warning.id)}
                          disabled={updateSettingsMutation.isPending}
                          title={warning.enabled !== false ? "Désactiver" : "Activer"}
                        >
                          {warning.enabled !== false ? (
                            <Eye className="h-4 w-4" />
                          ) : (
                            <EyeOff className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openEditWarningDialog(warning)}
                          disabled={updateSettingsMutation.isPending}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => handleDeleteWarning(warning.id)}
                          disabled={updateSettingsMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Info box */}
          <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/50 text-sm">
            <Info className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
            <div className="text-muted-foreground space-y-2">
              <p>
                <strong>Syntaxe des conditions :</strong> Utilisez les slugs des champs avec des opérateurs arithmétiques 
                (<code>+</code>, <code>-</code>, <code>*</code>, <code>/</code>) et de comparaison 
                (<code>&gt;</code>, <code>&lt;</code>, <code>&gt;=</code>, <code>&lt;=</code>, <code>==</code>, <code>!=</code>).
              </p>
              <p>
                <strong>Exemples :</strong>{" "}
                <code className="text-xs">nb_colis + nb_palettes &gt; 20</code>,{" "}
                <code className="text-xs">prix_unitaire * quantite &gt; 1000</code>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Resource Dialog */}
      <Dialog open={resourceDialogOpen} onOpenChange={setResourceDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingResource ? "Modifier la ressource" : "Ajouter une ressource"}
            </DialogTitle>
            <DialogDescription>
              {editingResource 
                ? "Modifiez les informations de la ressource."
                : "Ajoutez un lien externe ou uploadez un fichier depuis votre ordinateur."}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Resource Input Mode (only for new resources) */}
            {!editingResource && (
              <div className="space-y-2">
                <Label>Type de ressource</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => handleResourceInputModeChange("link")}
                    className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-colors ${
                      resourceInputMode === "link"
                        ? "border-primary bg-primary/5"
                        : "border-muted hover:border-muted-foreground/50"
                    }`}
                  >
                    <LinkIcon className={`h-5 w-5 ${resourceInputMode === "link" ? "text-primary" : "text-muted-foreground"}`} />
                    <span className={`font-medium text-sm ${resourceInputMode === "link" ? "text-primary" : ""}`}>
                      Lien externe
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleResourceInputModeChange("upload")}
                    className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-colors ${
                      resourceInputMode === "upload"
                        ? "border-primary bg-primary/5"
                        : "border-muted hover:border-muted-foreground/50"
                    }`}
                  >
                    <Upload className={`h-5 w-5 ${resourceInputMode === "upload" ? "text-primary" : "text-muted-foreground"}`} />
                    <span className={`font-medium text-sm ${resourceInputMode === "upload" ? "text-primary" : ""}`}>
                      Fichier (upload)
                    </span>
                  </button>
                </div>
              </div>
            )}
            
            {/* File Upload Zone (only for upload mode) */}
            {resourceInputMode === "upload" && !editingResource && (
              <div className="space-y-2">
                <Label>Fichier</Label>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={handleFileUpload}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.gif,.webp,.svg,.zip,.rar,.7z"
                />
                {uploadedFileInfo ? (
                  <div className="flex items-center gap-3 p-3 rounded-lg border bg-success/10 border-success/30">
                    <FileText className="h-8 w-8 text-success" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{uploadedFileInfo.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatFileSize(uploadedFileInfo.size)} • {getFileTypeLabel(uploadedFileInfo.type)}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                    >
                      Changer
                    </Button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    className="w-full p-6 border-2 border-dashed rounded-lg hover:border-primary/50 hover:bg-muted/30 transition-colors text-center"
                  >
                    {isUploading ? (
                      <div className="flex flex-col items-center gap-2">
                        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        <p className="text-sm text-muted-foreground">Upload en cours...</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <Upload className="h-8 w-8 text-muted-foreground" />
                        <p className="font-medium text-sm">Cliquez pour sélectionner un fichier</p>
                        <p className="text-xs text-muted-foreground">
                          PDF, Word, Excel, PowerPoint, images, archives...
                        </p>
                      </div>
                    )}
                  </button>
                )}
              </div>
            )}
            
            {/* Resource Title */}
            <div className="space-y-2">
              <Label htmlFor="resource-title">Titre *</Label>
              <Input
                id="resource-title"
                placeholder="Ex: Guide d'utilisation"
                value={newResource.title}
                onChange={(e) => setNewResource({ ...newResource, title: e.target.value })}
              />
            </div>
            
            {/* Resource Description */}
            <div className="space-y-2">
              <Label htmlFor="resource-description">Description (optionnel)</Label>
              <Input
                id="resource-description"
                placeholder="Brève description de la ressource"
                value={newResource.description}
                onChange={(e) => setNewResource({ ...newResource, description: e.target.value })}
              />
            </div>
            
            {/* Resource URL (only for link mode or editing) */}
            {(resourceInputMode === "link" || editingResource) && (
              <div className="space-y-2">
                <Label htmlFor="resource-url">
                  {newResource.type === "link" ? "URL du lien *" : "URL du fichier *"}
                </Label>
                <Input
                  id="resource-url"
                  placeholder={newResource.type === "link" 
                    ? "https://example.com/page" 
                    : "https://example.com/document.pdf"}
                  value={newResource.url}
                  onChange={(e) => setNewResource({ ...newResource, url: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  {newResource.type === "link"
                    ? "Entrez l'URL complète du lien externe."
                    : "Entrez l'URL directe vers le fichier téléchargeable."}
                </p>
              </div>
            )}
            
            {/* File Name (for file type when editing) */}
            {newResource.type === "file" && editingResource && (
              <div className="space-y-2">
                <Label htmlFor="resource-filename">Nom du fichier (optionnel)</Label>
                <Input
                  id="resource-filename"
                  placeholder="document.pdf"
                  value={newResource.file_name || ""}
                  onChange={(e) => setNewResource({ ...newResource, file_name: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  Nom affiché lors du téléchargement.
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setResourceDialogOpen(false)}>
              Annuler
            </Button>
            <Button 
              onClick={editingResource ? handleUpdateResource : handleAddResource}
              disabled={
                !newResource.title || 
                (resourceInputMode === "link" && !newResource.url) ||
                (resourceInputMode === "upload" && !uploadedFileInfo && !editingResource) ||
                isUploading
              }
            >
              {editingResource ? "Enregistrer" : "Ajouter"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Glossary Term Dialog */}
      <Dialog open={glossaryDialogOpen} onOpenChange={setGlossaryDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingTerm ? "Modifier le terme" : "Ajouter un terme"}
            </DialogTitle>
            <DialogDescription>
              {editingTerm 
                ? "Modifiez la définition du terme."
                : "Définissez un nouveau terme pour le glossaire."}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Term */}
            <div className="space-y-2">
              <Label htmlFor="term-name">Terme *</Label>
              <Input
                id="term-name"
                placeholder="Ex: Taux de marge"
                value={newTerm.term}
                onChange={(e) => setNewTerm({ ...newTerm, term: e.target.value })}
              />
            </div>
            
            {/* Definition */}
            <div className="space-y-2">
              <Label htmlFor="term-definition">Définition *</Label>
              <Textarea
                id="term-definition"
                placeholder="Entrez la définition du terme..."
                value={newTerm.definition}
                onChange={(e) => setNewTerm({ ...newTerm, definition: e.target.value })}
                rows={4}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setGlossaryDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={editingTerm ? handleUpdateTerm : handleAddTerm}>
              {editingTerm ? "Enregistrer" : "Ajouter"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Cross-Field Warning Dialog */}
      <Dialog open={warningDialogOpen} onOpenChange={setWarningDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {editingWarning ? "Modifier la règle" : "Ajouter une règle d'avertissement"}
            </DialogTitle>
            <DialogDescription>
              {editingWarning 
                ? "Modifiez la condition et le message d'avertissement."
                : "Définissez une condition et le message à afficher si elle est vraie."}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4 overflow-y-auto flex-1">
            {/* Condition */}
            <div className="space-y-2">
              <Label htmlFor="warning-condition">Condition *</Label>
              <Input
                id="warning-condition"
                placeholder="Ex: nb_colis + nb_palettes > 20"
                value={newWarning.condition}
                onChange={(e) => setNewWarning({ ...newWarning, condition: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Utilisez les slugs des champs. Opérateurs supportés: +, -, *, /, &gt;, &lt;, &gt;=, &lt;=, ==, !=, &amp;&amp;, ||
              </p>
            </div>
            
            {/* Message */}
            <div className="space-y-2">
              <Label htmlFor="warning-message">Message *</Label>
              <Textarea
                id="warning-message"
                placeholder="Ex: Attention, le volume total est trop important"
                value={newWarning.message}
                onChange={(e) => setNewWarning({ ...newWarning, message: e.target.value })}
                rows={2}
              />
            </div>
            
            {/* Severity */}
            <div className="space-y-3">
              <Label>Sévérité *</Label>
              <RadioGroup
                value={newWarning.severity ?? "warning"}
                onValueChange={(value) => setNewWarning({ ...newWarning, severity: value as WarningSeverity })}
                className="flex flex-col gap-3"
              >
                <div className="flex items-center space-x-3 p-3 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors">
                  <RadioGroupItem value="warning" id="severity-warning" />
                  <div className="flex-1">
                    <Label htmlFor="severity-warning" className="cursor-pointer font-medium">
                      Avertissement (non bloquant)
                    </Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Affiche un message d&apos;avertissement mais n&apos;empêche pas le calcul.
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-3 rounded-lg border border-destructive/30 cursor-pointer hover:bg-destructive/5 transition-colors">
                  <RadioGroupItem value="error" id="severity-error" />
                  <div className="flex-1">
                    <Label htmlFor="severity-error" className="cursor-pointer font-medium text-destructive">
                      Erreur (bloquant)
                    </Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Bloque le calcul et empêche la soumission tant que la condition est remplie.
                    </p>
                  </div>
                </div>
              </RadioGroup>
            </div>
            
            {/* Display Mode - hidden for blocking errors (always inline) */}
            {newWarning.severity !== "error" && (
            <div className="space-y-3">
              <Label>Mode d&apos;affichage *</Label>
              <RadioGroup
                value={newWarning.display_mode ?? "inline"}
                onValueChange={(value) => setNewWarning({ ...newWarning, display_mode: value as WarningDisplayMode })}
                className="flex flex-col gap-3"
              >
                <div className="flex items-center space-x-3 p-3 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors">
                  <RadioGroupItem value="inline" id="display-inline" />
                  <div className="flex-1">
                    <Label htmlFor="display-inline" className="cursor-pointer font-medium">
                      Sous les champs (inline)
                    </Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      L&apos;avertissement s&apos;affiche sous les champs sélectionnés lors de la saisie.
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-3 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors">
                  <RadioGroupItem value="results_only" id="display-results" />
                  <div className="flex-1">
                    <Label htmlFor="display-results" className="cursor-pointer font-medium">
                      Après calcul uniquement
                    </Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      L&apos;avertissement s&apos;affiche uniquement dans la section des résultats après le calcul.
                    </p>
                  </div>
                </div>
              </RadioGroup>
            </div>
            )}
            
            {/* Target and Trigger Fields - shown for inline mode and always for blocking errors */}
            {(newWarning.severity === "error" || newWarning.display_mode !== "results_only") && (
            <div className="grid grid-cols-2 gap-4">
              {/* Target Fields */}
              <div className="space-y-2">
                <Label>Afficher sous les champs *</Label>
                <Popover open={targetFieldsOpen} onOpenChange={setTargetFieldsOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={targetFieldsOpen}
                      className="w-full justify-between font-normal"
                    >
                      {newWarning.target_fields?.length 
                        ? `${newWarning.target_fields.length} champ(s) sélectionné(s)`
                        : "Sélectionner les champs..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[350px] p-0" align="start">
                    <Command shouldFilter={false}>
                      <CommandInput 
                        placeholder="Rechercher un champ..." 
                        value={targetFieldsSearch}
                        onValueChange={setTargetFieldsSearch}
                      />
                      <CommandList>
                        <CommandEmpty>Aucun champ trouvé.</CommandEmpty>
                        <CommandGroup className="max-h-64 overflow-y-auto">
                          {filteredTargetFields.map((field) => (
                            <CommandItem
                              key={field.slug}
                              value={field.slug}
                              onSelect={() => handleTargetFieldToggle(field.slug)}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  newWarning.target_fields?.includes(field.slug) ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                                <span className="truncate">{field.label}</span>
                                <span className="text-xs text-muted-foreground truncate">{field.slug}</span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {newWarning.target_fields && newWarning.target_fields.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {newWarning.target_fields.map(slug => {
                      const field = availableFields.find(f => f.slug === slug);
                      return (
                        <Badge 
                          key={slug} 
                          variant="secondary" 
                          className="text-xs cursor-pointer hover:bg-destructive/20"
                          onClick={() => handleTargetFieldToggle(slug)}
                        >
                          {field?.label || slug}
                          <X className="ml-1 h-3 w-3" />
                        </Badge>
                      );
                    })}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  L&apos;avertissement sera affiché sous ces champs.
                </p>
              </div>
              
              {/* Trigger Fields */}
              <div className="space-y-2">
                <Label>Déclenché au blur de *</Label>
                <Popover open={triggerFieldsOpen} onOpenChange={setTriggerFieldsOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={triggerFieldsOpen}
                      className="w-full justify-between font-normal"
                    >
                      {newWarning.trigger_fields?.length 
                        ? `${newWarning.trigger_fields.length} champ(s) sélectionné(s)`
                        : "Sélectionner les champs..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[350px] p-0" align="start">
                    <Command shouldFilter={false}>
                      <CommandInput 
                        placeholder="Rechercher un champ..." 
                        value={triggerFieldsSearch}
                        onValueChange={setTriggerFieldsSearch}
                      />
                      <CommandList>
                        <CommandEmpty>Aucun champ trouvé.</CommandEmpty>
                        <CommandGroup className="max-h-64 overflow-y-auto">
                          {filteredTriggerFields.map((field) => (
                            <CommandItem
                              key={field.slug}
                              value={field.slug}
                              onSelect={() => handleTriggerFieldToggle(field.slug)}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  newWarning.trigger_fields?.includes(field.slug) ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                                <span className="truncate">{field.label}</span>
                                <span className="text-xs text-muted-foreground truncate">{field.slug}</span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {newWarning.trigger_fields && newWarning.trigger_fields.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {newWarning.trigger_fields.map(slug => {
                      const field = availableFields.find(f => f.slug === slug);
                      return (
                        <Badge 
                          key={slug} 
                          variant="secondary" 
                          className="text-xs cursor-pointer hover:bg-destructive/20"
                          onClick={() => handleTriggerFieldToggle(slug)}
                        >
                          {field?.label || slug}
                          <X className="ml-1 h-3 w-3" />
                        </Badge>
                      );
                    })}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  La condition sera évaluée au blur.
                </p>
              </div>
            </div>
            )}
            
            {/* Enabled Toggle */}
            <div className="flex items-center justify-between p-3 rounded-lg border">
              <div className="space-y-0.5">
                <Label htmlFor="warning-enabled" className="cursor-pointer">Règle activée</Label>
                <p className="text-xs text-muted-foreground">
                  Désactivez pour garder la règle sans l&apos;appliquer.
                </p>
              </div>
              <Switch
                id="warning-enabled"
                checked={newWarning.enabled ?? true}
                onCheckedChange={(checked) => setNewWarning({ ...newWarning, enabled: checked })}
              />
            </div>
          </div>
          
          <DialogFooter className="flex-shrink-0">
            <Button variant="outline" onClick={() => setWarningDialogOpen(false)}>
              Annuler
            </Button>
            <Button 
              onClick={editingWarning ? handleUpdateWarning : handleAddWarning}
              disabled={
                !newWarning.condition || 
                !newWarning.message || 
                (newWarning.display_mode !== "results_only" && (
                  !newWarning.target_fields?.length ||
                  !newWarning.trigger_fields?.length
                ))
              }
            >
              {editingWarning ? "Enregistrer" : "Ajouter"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

